import { useTranslation } from 'next-i18next'
import { useEffect, useMemo, useState } from 'react'
import { useNextLocale } from '@captaincause/lib/useNextLocale'
import { useOnMount } from '@captaincause/lib/useOnMount'
import {
  RegionsFrance,
  RegionsOutreMer,
  RegionsWorld,
} from '@captaincause/types'
import { Icon } from '@captaincause/ui/Icon'
import { Panel } from '@captaincause/ui/Modals/Panel'
import { Map } from '@captaincause/ui/ProjectRegion/Map'
import { tm } from '@captaincause/ui/tm'
import { Button } from 'components/Button'
import type { Action, State } from './Filtersbar'
import type {
  Project,
  ProjectFilters,
  ProjectRegion,
  Region,
  RegionFrance,
  RegionOutreMer,
  RegionWorld,
} from '@captaincause/types'
import type { Dispatch, ReactNode } from 'react'

interface FilterPanelProps {
  togglePanelVisibility: () => void
  panelVisible: boolean
  removeAllFilters: () => void
  filters: ProjectFilters
  dispatch: Dispatch<Action>
  searching: boolean
  state: State
  projects: Project[]
}

const FRANCE = 'France'
const INTERNATIONAL = 'International'
const OUTREMER = 'Outre-Mer'

const mainRegionList = [FRANCE, INTERNATIONAL, OUTREMER]

const FilterDropDown = ({
  title,
  children,
  open = false,
  activeFilter,
  className = '',
}: {
  title: string
  children: ReactNode
  open?: boolean
  activeFilter: number
  className?: string
}) => {
  const [isOpen, setIsOpen] = useState(open)

  useOnMount(() => {
    setIsOpen(open)
  })

  return (
    <div className="w-full border-t-[1px] border-medium-gray p-4 last-of-type:border-b-[1px]">
      <Button
        variant="naked"
        iconClassName={tm(
          'right-[6px] scale-150 rotate-[270deg]',
          isOpen && 'rotate-180',
        )}
        iconRight="chevron"
        innerClassName="text-left w-full"
        className="h-auto w-full p-0 py-2 text-sm font-semibold !outline-none hover:bg-transparent focus:bg-transparent active:bg-transparent"
        onClick={() => setIsOpen(prev => !prev)}
      >
        {title}{' '}
        {activeFilter > 0 ? (
          <span className="font-normal text-dark-gray">({activeFilter})</span>
        ) : null}
      </Button>

      <div
        className={tm(
          'z-0 h-auto max-h-0 overflow-hidden transition-max-height duration-300',
          isOpen && 'max-h-140 overflow-scroll transition-max-height',
        )}
      >
        <div
          className={tm('mt-4 grid h-full grid-cols-3 gap-2 p-2', className)}
        >
          {children}
        </div>
      </div>
    </div>
  )
}

interface RegionListType {
  france: ProjectRegion[]
  international: ProjectRegion[]
  outreMer: ProjectRegion[]
}

type MainRegion = keyof RegionListType

function toggleRegion(state: ProjectRegion[], currentRegion: ProjectRegion) {
  if (state.some(region => region.id === currentRegion.id)) {
    return state.filter(region => region.id !== currentRegion.id)
  }

  return [...state, currentRegion]
}

export function FilterPanel({
  togglePanelVisibility,
  panelVisible,
  removeAllFilters,
  filters,
  dispatch,
  state,
  projects,
}: FilterPanelProps) {
  const { t } = useTranslation('filters.component')
  const locale = useNextLocale()

  const franceTag = filters.regions.find(region => region.name === FRANCE)!
  const internationalTag = filters.regions.find(
    region => region.name === INTERNATIONAL,
  )!

  const regionsWithAtLeastOneProject = useMemo(
    () =>
      filters.regions.filter(region =>
        projects.some(project => {
          const hasRegionTagList =
            project.region_tags &&
            Array.isArray(project.region_tags) &&
            project.region_tags.length !== 0
          if (hasRegionTagList) {
            return project.region_tags?.includes(region.name)
          }

          return project.region_tag === region.name
        }),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filters.regions],
  )

  const regionWithParent = useMemo(
    () =>
      regionsWithAtLeastOneProject
        .filter(region => region.parent_region_id !== undefined)
        .map(region => {
          const parentName: Region | '' =
            filters.regions.find(item => item.id === region.parent_region_id)
              ?.name || ''

          return { ...region, parentName }
        }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [regionsWithAtLeastOneProject],
  )

  const [activeMainRegion, setActiveMainRegion] = useState<ProjectRegion[]>([])
  const [activeRegion, setActiveRegion] = useState<ProjectRegion[]>([])

  useEffect(() => {
    // we use filters.regions because we need to know the mainRegionID (ie: franceTagID),
    // even if no project has france tag
    const activeMainRegions = filters.regions.filter(
      region =>
        mainRegionList.includes(region.name) &&
        state.region.includes(region.id.toString()),
    )

    const activeRegions = regionWithParent.filter(
      region =>
        state.region.includes(region.id.toString()) &&
        !mainRegionList.includes(region.name),
    )

    setActiveMainRegion(activeMainRegions)
    setActiveRegion(activeRegions)
  }, [regionWithParent, state.region, filters.regions])

  const numberOfActiveRegionFilters = state.region.length
  const numberOfActiveFilters = numberOfActiveRegionFilters

  const regionList: RegionListType = {
    france: [],
    international: [],
    outreMer: [],
  }

  regionWithParent.forEach(region => {
    if (
      RegionsFrance.includes(region.name as RegionFrance) &&
      region.name !== FRANCE
    ) {
      regionList.france.push(region)
    }
    if (
      RegionsWorld.includes(region.name as RegionWorld) &&
      region.name !== INTERNATIONAL
    ) {
      regionList.international.push(region)
    }
    if (
      RegionsOutreMer.includes(region.name as RegionOutreMer) &&
      region.name !== OUTREMER
    ) {
      regionList.outreMer.push(region)
    }
  })

  const hasFranceRegion = !!regionWithParent.find(
    region => region.name === FRANCE || region.parentName === FRANCE,
  )
  const hasInternationalRegion = !!regionWithParent.find(
    region =>
      region.name === INTERNATIONAL || region.parentName === INTERNATIONAL,
  )
  const reset = () => {
    removeAllFilters()
    setActiveRegion([])
  }

  const clickOnRegion = (region: ProjectRegion) => {
    setActiveRegion(prev => toggleRegion(prev, region))

    if (activeMainRegion.length !== 0) {
      dispatch({
        type: 'ONLY_ONE_TAG',
        category: 'region',
        tag: region.id.toString(),
      })
      setActiveMainRegion([])
    } else {
      dispatch({
        type: 'TOGGLE_FILTER_TAG',
        category: 'region',
        tag: region.id.toString(),
      })
    }
  }

  return (
    <Panel
      orientation="right"
      close={togglePanelVisibility}
      locale={locale}
      isOpen={panelVisible}
      headerClassName="hidden"
      panelClassName="p-0 flex flex-col max-w-[385px]"
      rootNode="__next"
    >
      {({ onClose }) => (
        <>
          <div className="mb-20 flex-1">
            <div className="flex flex-col items-center justify-between gap-4 p-4">
              <div className="flex w-full items-center justify-between">
                <div className="text-sm font-semibold leading-6">
                  {t('regions')}{' '}
                  {numberOfActiveFilters > 0 ? (
                    <span className="font-normal">
                      ({numberOfActiveFilters})
                    </span>
                  ) : null}
                </div>
                <div className="flex items-center">
                  <Button
                    variant="tertiary"
                    ariaLabel="Reset filters"
                    innerClassName="text-dark-gray border-dark-gray"
                    className="mr-2 border-dark-gray text-sm font-normal text-dark-gray focus:bg-transparent"
                    onClick={reset}
                    style={{ color: 'dark-gray' }}
                  >
                    {t('clear')}
                  </Button>
                  <Button
                    variant="naked"
                    ariaLabel="Close filters section"
                    onClick={onClose}
                  >
                    <Icon icon="cross" fill="fill-black" size="medium" />
                  </Button>
                </div>
              </div>
              <div className="flex w-full flex-col gap-2">
                {hasFranceRegion && (
                  <Button
                    variant="naked"
                    innerClassName="w-full flex items-center gap-[12px] text-sm font-medium leading-6 p-2 "
                    className={tm(
                      'focus:bg-inherit w-full border-2 border-transparent transition-border duration-300 ease-out hover:bg-light-gray active:bg-transparent',
                      activeMainRegion.some(
                        regionObj => regionObj.id === franceTag.id,
                      ) && 'border-purple bg-transparent hover:bg-light-gray',
                    )}
                    onClick={() => {
                      setActiveMainRegion(prev => toggleRegion(prev, franceTag))
                      if (activeRegion.length !== 0) {
                        dispatch({
                          type: 'ONLY_ONE_TAG',
                          category: 'region',
                          tag: franceTag.id.toString(),
                        })
                        setActiveRegion([])
                      } else {
                        dispatch({
                          type: 'TOGGLE_FILTER_TAG',
                          category: 'region',
                          tag: franceTag.id.toString(),
                        })
                      }
                    }}
                  >
                    <Icon
                      icon="france"
                      size="small"
                      className="stroke-dark-gray"
                    />
                    {t('in-france')}
                  </Button>
                )}
                {hasInternationalRegion && (
                  <Button
                    variant="naked"
                    innerClassName="flex items-center gap-[12px] text-sm font-medium leading-6 p-2 w-full"
                    className={tm(
                      'focus:bg-inherit w-full border-2 border-transparent transition-border duration-300 ease-out hover:bg-light-gray active:bg-transparent',
                      activeMainRegion.some(
                        regionObj => regionObj.id === internationalTag.id,
                      ) && 'border-purple bg-transparent hover:bg-light-gray',
                    )}
                    onClick={() => {
                      setActiveMainRegion(prev =>
                        toggleRegion(prev, internationalTag),
                      )
                      if (activeRegion.length !== 0) {
                        dispatch({
                          type: 'ONLY_ONE_TAG',
                          category: 'region',
                          tag: internationalTag.id.toString(),
                        })
                        setActiveRegion([])
                      } else {
                        dispatch({
                          type: 'TOGGLE_FILTER_TAG',
                          category: 'region',
                          tag: internationalTag.id.toString(),
                        })
                      }
                    }}
                  >
                    <Icon icon="world" size="small" fill="fill-dark-gray" />
                    International
                  </Button>
                )}
              </div>
            </div>
            {(Object.keys(regionList) as MainRegion[])
              .filter(key => regionList[key].length !== 0)
              .map((mainRegion: MainRegion) => {
                const isFrance = mainRegion === FRANCE.toLowerCase()
                const isInternational =
                  mainRegion === INTERNATIONAL.toLowerCase()
                const title =
                  // eslint-disable-next-line no-nested-ternary
                  isFrance
                    ? t('france-area')
                    : isInternational
                    ? t('international-area')
                    : t('outreMer-area')

                const activeRegionFilter = activeRegion.filter(
                  (region: ProjectRegion) =>
                    regionList[mainRegion].map(r => r.id).includes(region.id),
                )

                if (regionList[mainRegion].length === 0) return null

                return (
                  <FilterDropDown
                    title={title}
                    activeFilter={activeRegionFilter.length}
                    className={tm(isInternational && 'grid-cols-2')}
                    key={title}
                    open={state.region.some(id =>
                      regionList[mainRegion]
                        .map(region => region.id.toString())
                        .includes(id),
                    )}
                  >
                    {regionList[mainRegion].map(region => (
                      <Button
                        variant="raw"
                        onClick={() => clickOnRegion(region)}
                        key={region.name}
                        className={tm(
                          'm-auto box-border w-full rounded-2xl p-2 !outline-none ring-1 ring-transparent transition-shadow duration-300 ease-out hover:ring-purple',
                          activeRegion.some(
                            regionObj => regionObj.name === region.name,
                          ) && 'ring-2 ring-purple',
                        )}
                        innerClassName="w-full"
                      >
                        <Map
                          type={mainRegion}
                          highlight={[region.name]}
                          label={region.name}
                          className="w-full"
                          locale={locale}
                        />
                      </Button>
                    ))}
                  </FilterDropDown>
                )
              })}
            {/* Regions filter on desktop  */}
          </div>

          <div className="sticky bottom-0 left-0 w-full bg-white p-4 shadow-2xl">
            <Button
              className="max-h-10 w-full rounded-3xl text-base"
              variant="primary"
              onClick={onClose}
            >{`${t('see-results')} (${projects.length})`}</Button>
          </div>
          {/* --  */}
        </>
      )}
    </Panel>
  )
}
